import React, { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  MoreHorizontal,
  Edit,
  Trash2,
  Plus,
  Tag,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/common/DataTable';
import { useNotifications } from '@/hooks/useNotifications';
import {
  useAdminCategories,
  useCreateMerchantCategory,
  useUpdateCategoryStatus,
  useUpdateCategoryName,
  useDeleteCategory,
} from '@/hooks/useVendors';
import { MerchantCategory } from '@/api/vendors';
import { formatDate } from '@/utils';

export function CategoriesPage() {
  const { success, error: showError } = useNotifications();

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState<MerchantCategory | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [addError, setAddError] = useState('');
  const [editError, setEditError] = useState('');

  // Fetching categories (fetching all for clean client-side DataTable operations)
  const { data: response, isLoading } = useAdminCategories({ skip: 0, take: 1000 });
  const categories = response?.data ?? [];

  // Mutations
  const { mutate: createCategory, isPending: isCreating } = useCreateMerchantCategory();
  const { mutate: updateStatus, isPending: isTogglingStatus } = useUpdateCategoryStatus();
  const { mutate: updateName, isPending: isUpdatingName } = useUpdateCategoryName();
  const { mutate: deleteCategory, isPending: isDeleting } = useDeleteCategory();

  const handleToggleStatus = (categoryId: number, isActive: boolean) => {
    updateStatus(
      { categoryId, isActive },
      {
        onSuccess: () => {
          success(`Category status updated to ${isActive ? 'active' : 'inactive'}.`);
        },
        onError: (err) => {
          showError(err.response?.data?.message || 'Failed to update status.');
        },
      }
    );
  };

  const handleOpenAdd = () => {
    setCategoryName('');
    setAddError('');
    setAddDialogOpen(true);
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const name = categoryName.trim();
    if (!name) {
      setAddError('Category name is required.');
      return;
    }
    if (name.length < 2) {
      setAddError('Category name must be at least 2 characters.');
      return;
    }
    if (name.length > 50) {
      setAddError('Category name must be 50 characters or less.');
      return;
    }
    setAddError('');

    createCategory(
      { name },
      {
        onSuccess: () => {
          success(`Category "${name}" created successfully.`);
          setAddDialogOpen(false);
        },
        onError: (err) => {
          showError(err.response?.data?.message || 'Failed to create category.');
        },
      }
    );
  };

  const handleOpenEdit = (category: MerchantCategory) => {
    setSelectedCategory(category);
    setCategoryName(category.name);
    setEditError('');
    setEditDialogOpen(true);
  };

  const handleEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategory) return;
    const name = categoryName.trim();
    if (!name) {
      setEditError('Category name is required.');
      return;
    }
    if (name.length < 2) {
      setEditError('Category name must be at least 2 characters.');
      return;
    }
    if (name.length > 50) {
      setEditError('Category name must be 50 characters or less.');
      return;
    }
    setEditError('');

    updateName(
      { categoryId: selectedCategory.id, name },
      {
        onSuccess: () => {
          success(`Category updated to "${name}".`);
          setEditDialogOpen(false);
        },
        onError: (err) => {
          showError(err.response?.data?.message || 'Failed to update category name.');
        },
      }
    );
  };

  const handleOpenDelete = (category: MerchantCategory) => {
    setSelectedCategory(category);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    if (!selectedCategory) return;

    deleteCategory(selectedCategory.id, {
      onSuccess: () => {
        success(`Category "${selectedCategory.name}" has been deleted.`);
        setDeleteDialogOpen(false);
      },
      onError: (err) => {
        showError(err.response?.data?.message || 'Failed to delete category.');
      },
    });
  };

  function CategoryActions({ category }: { category: MerchantCategory }) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => handleOpenEdit(category)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit name
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => handleOpenDelete(category)} className="text-destructive">
            <Trash2 className="mr-2 h-4 w-4" />
            Delete category
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const columns: ColumnDef<MerchantCategory>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      cell: ({ row }) => <span className="font-mono text-muted-foreground">{row.original.id}</span>,
    },
    {
      accessorKey: 'name',
      header: 'Category Name',
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <Tag className="h-4 w-4 text-muted-foreground/60" />
          <span className="font-semibold text-foreground">{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => {
        const category = row.original;
        const isActive = category.isActive ?? false;
        return (
          <div className="flex items-center space-x-2">
            <Switch
              checked={isActive}
              onCheckedChange={(checked) => handleToggleStatus(category.id, checked)}
              disabled={isTogglingStatus}
            />
            <span className={`text-xs font-semibold ${isActive ? 'text-green-500' : 'text-muted-foreground'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => <span>{formatDate(row.original.createdAt)}</span>,
    },
    {
      id: 'actions',
      cell: ({ row }) => <CategoryActions category={row.original} />,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Categories</h2>
          <p className="text-muted-foreground">Manage vendor industry categories and status.</p>
        </div>
        <Button onClick={handleOpenAdd}>
          <Plus className="mr-2 h-4 w-4" /> Add Category
        </Button>
      </div>

      <div className="rounded-lg border bg-card p-6 shadow-sm">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <span className="text-muted-foreground animate-pulse">Loading categories...</span>
          </div>
        ) : (
          <DataTable
            data={categories}
            columns={columns}
            searchPlaceholder="Search categories..."
          />
        )}
      </div>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Add Category</DialogTitle>
              <DialogDescription>
                Create a new industry category for vendor partners.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="add-name">Name</Label>
                <Input
                  id="add-name"
                  value={categoryName}
                  onChange={(e) => {
                    setCategoryName(e.target.value);
                    if (e.target.value.trim()) setAddError('');
                  }}
                  placeholder="e.g. Health & Wellness"
                  className={addError ? 'border-destructive' : ''}
                  disabled={isCreating}
                />
                {addError && (
                  <p className="text-sm text-destructive">{addError}</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddDialogOpen(false)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <form onSubmit={handleEdit}>
            <DialogHeader>
              <DialogTitle>Edit Category Name</DialogTitle>
              <DialogDescription>
                Change the name of the selected category.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={categoryName}
                  onChange={(e) => {
                    setCategoryName(e.target.value);
                    if (e.target.value.trim()) setEditError('');
                  }}
                  className={editError ? 'border-destructive' : ''}
                  disabled={isUpdatingName}
                />
                {editError && (
                  <p className="text-sm text-destructive">{editError}</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                disabled={isUpdatingName}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdatingName}>
                {isUpdatingName ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete category{' '}
              <span className="font-semibold text-foreground">
                "{selectedCategory?.name}"
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
